import { createContext, useCallback, useContext, useMemo, useReducer } from "react";

const JobBoardSearchContext = createContext();

const initialState = {
    searchParams: {},
    results: [],
    loading: false,
    error: null,
    totalResults: 0,
    pagination: {
        current: 1,
        pageSize: 20,
        total: 0
    },
    lastViewedCandidate: null
}


function searchReducer(state, action) {


    switch(action.type) {
        case 'SET_SEARCH_PARAMS': {
            const newParams = action.payload || {};
            if (JSON.stringify(state.searchParams) === JSON.stringify(newParams)) {
                return state;
            }
            return {...state, searchParams: newParams};
        }
            
        case 'SET_RESULTS': {
            const newResults = action.payload?.results || [];
            const newTotal = action.payload?.total || 0;
            
            if (JSON.stringify(state.results) === JSON.stringify(newResults) &&
                state.totalResults === newTotal) {
                return state;
            }
            return {
                ...state, 
                results: newResults,
                totalResults: newTotal,
                pagination: {
                    ...state.pagination,
                    total: newTotal
                }
            };
        }
            
        case 'SET_LOADING': {
            const newLoading = !!action.payload;
            if (state.loading === newLoading) {
                return state;
            }
            return {...state, loading: newLoading};
        }
            
        case 'SET_ERROR': {
            const newError = action.payload || null;
            if (state.error === newError) {
                return state;
            }
            return {...state, error: newError};
        }
            
        case 'SET_PAGE': {
            const { current, pageSize = state.pagination.pageSize } = action.payload;
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    current,
                    pageSize
                }
            };
        }

        case 'SET_PAGE_SIZE': {
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    pageSize: action.payload,
                    current: 1 
                }
            };
        }

        case 'SET_LAST_VIEWED_CANDIDATE': {
            return {
                ...state,
                lastViewedCandidate: action.payload
            };
        }
            
        case 'RESET_SEARCH': {
            if (JSON.stringify(state) === JSON.stringify(initialState)) {
                return state;
            }
            return {...initialState};
        }
            
        default:
            return state;
    }
}

export function JobBoardSearchProvider({children, initialSearchParams = {}}) {
    const [state, dispatch] = useReducer(searchReducer, {
        ...initialState,
        searchParams: initialSearchParams
    });


    const memoizedDispatch = useCallback((action) => {
        if (!action?.type) return;
        dispatch(action);
    }, []);

    const contextValue = useMemo(() => ({
        state,
        dispatch: memoizedDispatch
    }), [state, memoizedDispatch]);

    return (
        <JobBoardSearchContext.Provider value={contextValue}>
            {children}
        </JobBoardSearchContext.Provider>
    );
}

export function useSearchParams() {
    const context = useContext(JobBoardSearchContext);
    if (!context) throw new Error('useSearchParams must be used within a JobBoardSearchProvider');
    
    return useMemo(() => ({
        searchParams: context.state.searchParams,
        loading: context.state.loading,
        error: context.state.error,
        dispatch: context.dispatch
    }), [
        context.state.searchParams,
        context.state.loading,
        context.state.error,
        context.dispatch
    ]);
}

export function useSearchResults() {
    const context = useContext(JobBoardSearchContext);
    if (!context) throw new Error('useSearchResults must be used within a JobBoardSearchProvider');
    
    return useMemo(() => ({
        results: context.state.results,
        totalResults: context.state.totalResults,
        dispatch: context.dispatch
    }), [
        context.state.results,
        context.state.totalResults,
        context.dispatch
    ]);
}

export function useSearchPagination() {
    const context = useContext(JobBoardSearchContext);
    if (!context) throw new Error('useSearchPagination must be used within a JobBoardSearchProvider');
    
    return useMemo(() => ({
        pagination: context.state.pagination,
        lastViewedCandidate: context.state.lastViewedCandidate,
        dispatch: context.dispatch
    }), [
        context.state.pagination,
        context.state.lastViewedCandidate,
        context.dispatch
    ]);
}

export function useJobBoardSearch() {
    const context = useContext(JobBoardSearchContext);
    if (!context) throw new Error('useJobBoardSearch must be used within a JobBoardSearchProvider');
    
    return useMemo(() => ({
        state: {
            searchParams: context.state.searchParams,
            loading: context.state.loading,
            error: context.state.error,
            results: context.state.results,
            totalResults: context.state.totalResults,
            pagination: context.state.pagination,
            lastViewedCandidate: context.state.lastViewedCandidate
        },
        dispatch: context.dispatch
    }), [
        context.state.searchParams,
        context.state.loading,
        context.state.error,
        context.state.results,
        context.state.totalResults,
        context.state.pagination,
        context.state.lastViewedCandidate,
        context.dispatch
    ]);
}


